import { useEffect } from "react"
import { useLocation, Outlet } from "react-router-dom"
import { useTranslation } from "react-i18next"
// import { useQueryClient } from "@tanstack/react-query"
import { useViewMode } from "@/hooks/useViewMode"
import { useTotemDetailView } from "@/hooks/useTotemDetailView"
import { useOverlay } from "@/context/OverlayContext"
import { useNavbar } from "@/context/NavbarContext"
import { HeaderMobile } from "@/components/Header/HeaderMobile"
import { HeaderTotem } from "@/components/Header/HeaderTotem"
import { TotemBottomNav } from "@/components/TotemBottomNav/TotemBottomNav"
import { AppMenu } from "@/components/AppMenu/AppMenu"
import { ToastContainer } from "@/components/Toast/ToastContainer"
import { ALL_ROUTES, NAV_ITEMS } from "@/utils/constants"
// import { useTranslation } from "react-i18next"
// import { useNavigateWithParams } from "@/hooks/useNavigateWithParams"
import { ScrollToTop } from "@/components/ScrollToTop/ScrollToTop"
// import { artworkKeys } from "@/domains/artwork"
// import { roomKeys } from "@/domains/room"
// import { floorKeys } from "@/domains/floor"

/**
 * Layout wrapper that provides:
 * - Header with language switcher and view mode toggle
 * - Main content area (Outlet) with bottom padding for fixed menu
 * - Bottom navigation (AppMenu) - visible only on routes defined in NAV_ITEMS
 *
 * Also initializes view mode detection (?view=totem|mobile)
 * This hook applies data-mode="totem" attribute to <html> element
 *
 * Menu visibility logic:
 * - Shows menu if current path is in NAV_ITEMS
 * - Future: hide when in totem mode (via data-mode attribute)
 *
 * Cache invalidation:
 * - Invalidates all language-aware caches when language changes
 * - Ensures UI always shows data in the current language
 */
export function AppLayout() {
  // Initialize view mode from URL parameter
  useViewMode()
  const location = useLocation()
  const { t } = useTranslation()
  const { isTotemMode } = useViewMode()
  const { isTotemDetailView } = useTotemDetailView()
  const { isOverlayActive } = useOverlay()
  const { showBackButton } = useNavbar()
  // const navigate = useNavigateWithParams();
  //  const queryClient = useQueryClient()

   /**
    * Invalidate all language-aware caches when language changes
    * Ensures fresh data for the newly selected language
    */
  //  useEffect(() => {
  //     queryClient.invalidateQueries({
  //        queryKey: artworkKeys.all,
  //     })
  //     queryClient.invalidateQueries({
  //        queryKey: roomKeys.all,
  //     })
  //     queryClient.invalidateQueries({
  //        queryKey: floorKeys.all,
  //     })
  //  }, [i18n.language, queryClient])
  // useEffect(() => {
  //     if(isTotemMode) {
  //       console.log('%c TOTEM MODE ACTIVE','color: yellow;')
  //       console.log('- window.innerWidth', window.innerWidth)   // deve essere 1080
  //       console.log('- window.innerHeight', window.innerHeight)  // deve essere 1920
  //       console.log('- window.devicePixelRatio', window.devicePixelRatio) // deve essere 1
  //     }
  // }, [])

  // Check if current path is in NAV_ITEMS
  const isHome = location.pathname == ALL_ROUTES.basePath
  const isFirstLevelApp = NAV_ITEMS.some(item => item.path === location.pathname)
  const isQuizPage = location.pathname.includes(ALL_ROUTES.quiz)

  // WCAG 2.4.2: aggiorna document.title per le pagine di primo livello (NAV_ITEMS).
  // Le pagine con contenuto dinamico (quiz game, room detail, ecc.) gestiscono il proprio title.
  useEffect(() => {
    const currentNavItem = NAV_ITEMS.find(item => item.path === location.pathname)
    if (currentNavItem) {
      document.title = `${t(currentNavItem.labelKey)} — Palazzo Mantica`
    }
  }, [location.pathname, t])

  return (
    <div className="app-layout bg-background font-inter">
      {/* Reset scroll position on route change */}
      <ScrollToTop />

      {/* Header / bottom nav
          - Mobile: header always fixed at top
          - Totem non-detail pages (list, map, quiz…): header fixed at top
          - Totem detail pages (room / artwork): nav moved to fixed bottom bar
            for ergonomics on 49" portrait displays (content at waist/chest height) */}
      {isTotemMode ? (
        isTotemDetailView ? (
          // Room/Artwork detail: full-bleed hero, no top header, back nav at bottom
          <div className="fixed bottom-0 left-0 right-0 z-50">
            <TotemBottomNav />
          </div>
        ) : showBackButton ? (
          // Pages with back action (e.g. FeedDetail): logo-only header at top + back button at bottom
          <>
            <div className="fixed top-0 left-0 right-0 z-50">
              <HeaderTotem />
            </div>
            <div className="fixed bottom-0 left-0 right-0 z-50">
              <TotemBottomNav />
            </div>
          </>
        ) : (
          // First-level pages (Rooms, Map, Info…): header with logo at top, no back button
          <div className="fixed top-0 left-0 right-0 z-50">
            <HeaderTotem />
          </div>
        )
      ) : (
        <div className="fixed top-0 left-0 right-0 z-50">
          <HeaderMobile />
        </div>
      )}

      {/* Main content - padding managed by AppLayout via custom Tailwind spacing */}
      {/* On totem detail views (room/artwork), remove top padding for full-bleed hero */}
      {/* 
        ${isFirstLevelApp && !isTotemMode ? 'pb-m-bottom-nav' : ''}  x Appmenu mobile
        ${isFirstLevelApp && isTotemMode  ? 'pb-layout-bottom' : ''}  x Navbar totem (x pulsante indietro)
      */}
      <main
        className={`flex flex-col overflow-y-auto min-h-screen
              ${isTotemDetailView ? '' : 'mode-totem:pt-layout-top-with-nav'}
              ${isTotemDetailView || (isTotemMode && showBackButton) && !isQuizPage ? 'pb-t-bottom-nav' : ''}
              ${isFirstLevelApp && !isTotemMode ? 'pb-m-bottom-nav' : ''} 
              ${isFirstLevelApp && isTotemMode  ? 'pb-layout-bottom' : ''}
              ${isHome ? 'pt-layout-top' : ''}
            `}
      >
        <div className={`flex-1 ${isHome && isTotemMode ? 'flex': ''} `}>
          <Outlet />
        </div>
      </main>

      {/* Toast notifications */}
      <ToastContainer />

      {/* Bottom Navigation Menu - visible only if path is in NAV_ITEMS, hidden during overlays */}
      {isFirstLevelApp && !isTotemMode && !isOverlayActive && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <AppMenu />
        </div>
      )}

    </div>
  )
}
